import type { ExpenditureBudgetPurposeModel } from '@/features/master-table/models';
import { NEST_ITEM_CONNECTOR } from '@/utils';
import groupBy from 'lodash-es/groupBy';
import type z from 'zod';

type ExpenditureBudgetPurpose = z.infer<typeof ExpenditureBudgetPurposeModel>;

type EnhancedExpenditureBudgetPurpose = ExpenditureBudgetPurpose & {
  upperExpenditurePurposeId: string | null;
  upperExpenditurePurpose: ExpenditureBudgetPurpose['expenditurePurpose']['upper'];
};

/**
 * 目の配列を表示用文字列の配列にフォーマット
 * 各要素は "/" で連結された階層構造の文字列
 * * 一般会計：[会計表示名,所管名,組織名,項, 目]
 * * 特別会計：[会計表示名,所管名,勘定（あれば）,項, 目]
 */
export const formatExpenditureBudgetPurpose = (
  expenditureBudgetPurposes: ExpenditureBudgetPurpose[] | undefined | null,
): string[] | null => {
  if (!expenditureBudgetPurposes?.length) {
    return null;
  }
  return expenditureBudgetPurposes.map((purpose) =>
    [
      purpose.accountingType.name,
      purpose.jurisdiction.name,
      ...(purpose.accountingType.type === 'general'
        ? [purpose.organization?.name]
        : [purpose.accountTitle?.name]),
      purpose.expenditurePurpose.upper?.name ?? '',
      purpose.expenditurePurpose.name,
    ]
      .filter(Boolean)
      .join(NEST_ITEM_CONNECTOR),
  );
};

/**
 * TreeSelectコンポーネント用のID配列を構築
 * 一般会計: [会計ID, 所管ID, 組織ID, 項ID, 目ID]
 * 特別会計: [会計ID, 所管ID, 科目ID, 項ID, 目ID]
 */
const buildIdPath = (
  purpose: EnhancedExpenditureBudgetPurpose,
  jurisdictionId: string,
  upperExpenditurePurposeId: string,
): string[] => {
  const isGeneralAccounting = purpose.accountingType.type === 'general';

  return [
    purpose.accountingId,
    jurisdictionId,
    isGeneralAccounting ? purpose.organizationId : purpose.accountTitleId,
    upperExpenditurePurposeId,
    purpose.id,
  ].filter(Boolean) as string[];
};

/**
 * 会計種別に応じてグループ化し、ID配列を生成
 * 一般会計は組織ID、特別会計は会計IDでグループ化
 */
const processPurposes = (
  purposes: EnhancedExpenditureBudgetPurpose[],
  jurisdictionId: string,
): string[][] => {
  const isGeneralAccounting = purposes[0]?.accountingType.type === 'general';
  const groupingKey = isGeneralAccounting ? 'organizationId' : 'accountingId';

  const byGrouping = groupBy(purposes, groupingKey);

  return Object.entries(byGrouping).flatMap(
    ([_groupingId, groupedPurposes]) => {
      // さらに項IDでグループ化
      const byUpperPurpose = groupBy(
        groupedPurposes,
        'upperExpenditurePurposeId',
      );

      return Object.entries(byUpperPurpose).flatMap(
        ([upperPurposeId, upperPurposes]) =>
          upperPurposes.map((purpose) =>
            buildIdPath(purpose, jurisdictionId, upperPurposeId),
          ),
      );
    },
  );
};

/**
 * 会計種別でグループ化して処理を振り分け
 */
const processAccountingType = (
  purposes: EnhancedExpenditureBudgetPurpose[],
  jurisdictionId: string,
): string[][] => {
  const byAccountingType = groupBy(purposes, (p) => p.accountingType.type);
  return Object.entries(byAccountingType).flatMap(([_type, typePurposes]) =>
    processPurposes(typePurposes, jurisdictionId),
  );
};

/**
 * 目からTreeSelectコンポーネントの初期選択値となるID配列を生成
 * 階層構造: 所管 > 会計種別 > (組織|会計) > 項 > 目
 */
export const getInitialExpenditureBudgetPurposeIds = (
  expenditureBudgetPurposes: ExpenditureBudgetPurpose[] | undefined | null,
): string[][] => {
  if (!expenditureBudgetPurposes?.length) {
    return [];
  }

  /** 後続の処理のために項データを追加して拡張 */
  const enhancedPurposes = expenditureBudgetPurposes.map((purpose) => ({
    ...purpose,
    upperExpenditurePurposeId: purpose.expenditurePurpose.upperId,
    upperExpenditurePurpose: purpose.expenditurePurpose.upper,
  }));

  const byJurisdiction = groupBy(enhancedPurposes, 'jurisdictionId');

  return Object.entries(byJurisdiction).flatMap(([jurisdictionId, purposes]) =>
    processAccountingType(purposes, jurisdictionId),
  );
};
